import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Alert,
} from '@mui/material';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import { profileName } from '../../../utils/profileDisplay';

// Shared confirmation for deleting a data profile — used by the sidebar's three-dot
// menu and by ProfileDashboard's "Delete Profile" button, so the tombstone write
// lives in exactly one place.
function DeleteDataProfileDialog({ open, profile, companyId, tableId, onClose }) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState(null);

  // A failure belongs to one open/close cycle: the next open starts clean.
  useEffect(() => {
    setError(null);
  }, [open]);

  const handleConfirm = async () => {
    if (!companyId || !tableId || !profile?.id) return;
    setIsDeleting(true);
    setError(null);
    try {
      // Tombstone delete: status flips to 'Deleted'; nothing is removed, so nothing
      // can be resurrected. updateDoc (not setDoc-merge) fails loudly if the doc is gone.
      const profileRef = doc(db, `users/${companyId}/tables/${tableId}/data_profiles`, profile.id);
      await updateDoc(profileRef, { status: 'Deleted', deleted_at: serverTimestamp() });
      onClose();
    } catch (err) {
      console.error('Error deleting profile:', err);
      setError(`Failed to delete profile: ${err.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !isDeleting && onClose()}>
      <DialogTitle>Delete Data Profile</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <DialogContentText>
          Are you sure you want to delete the data profile "{profile ? profileName(profile) : ''}"? This action cannot be undone.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose()} disabled={isDeleting}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          color="error"
          disabled={isDeleting}
        >
          {isDeleting ? 'Deleting...' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DeleteDataProfileDialog;
